import React, { useState, useRef, useContext, useEffect } from 'react';
import ReactPlayer from 'react-player';
import styled from '@emotion/styled';
import { BsClock, BsCameraVideoFill } from 'react-icons/bs';
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/md';
import { VideoContext } from '../../App';
import './Video.css';

const VideoContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  margin: 2rem auto;
  max-width: 900px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const VideoHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const VideoTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
  font-weight: 600;
`;

const DescriptionButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2196f3;
  font-size: 0.9rem;
  padding: 0.5rem 0;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #1976d2;
  }
`;

const VideoDescription = styled.div`
  padding: 1.5rem;
  background: #f8f9fa;
  color: #495057;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;
  background: #000;
`;



const VideoInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const InfoItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 0.9rem;

  svg {
    color: #2196f3;
  }
`;

function Video({ e, index }) {
  const { activeVideoId, setActiveVideoId } = useContext(VideoContext);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);

  // إيقاف الفيديو عندما يتم تشغيل فيديو آخر
  useEffect(() => {
    if (activeVideoId !== index && isPlaying) {
      setIsPlaying(false);
    }
  }, [activeVideoId, index, isPlaying]);

  const formatTime = (seconds) => {
    if (!seconds) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProgress = (state) => {
    setCurrentTime(state.playedSeconds);
  };

  const handlePause = () => {
    setIsPlaying(false);
    if (activeVideoId === index) {
      setActiveVideoId(null);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (activeVideoId === index) {
      setActiveVideoId(null);
    }
  };

  return (
    <VideoContainer>
      <VideoHeader>
        <VideoTitle>{e.title}</VideoTitle>
        {e.description && (
          <DescriptionButton onClick={() => setShowDescription(!showDescription)}>
            {showDescription ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
            {showDescription ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
          </DescriptionButton>
        )}
      </VideoHeader>

      {showDescription && (
        <VideoDescription>
          <p>{e.description}</p>
        </VideoDescription>
      )}

      <VideoWrapper>
        <ReactPlayer
          ref={playerRef}
          url={e?.URLVideo}
          width="100%"
          height="100%"
          playing={isPlaying && activeVideoId === index}
          controls={true}
          onProgress={handleProgress}
          onPause={handlePause}
          onEnded={handleEnded}
          onPlay={() => {
            setActiveVideoId(index);
            setIsPlaying(true);
          }}
          progressInterval={100}
          style={{ position: 'absolute', top: 0, left: 0 }}
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload',
                onContextMenu: (e) => e.preventDefault()
              }
            },
            youtube: {
              playerVars: {
                modestbranding: 1,
                rel: 0
              }
            }
          }}
          stopOnUnmount={true}
        />
      </VideoWrapper>

      <VideoInfo>
        <InfoItem>
          <BsClock size={16} />
          {formatTime(currentTime)}
        </InfoItem>
        <InfoItem>
          <BsCameraVideoFill size={16} />
          {e.quality || 'HD'}
        </InfoItem>
      </VideoInfo>
    </VideoContainer>
  );
}

export default Video;
